/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { TableWrapper } from '../../../../styles/table/TableWrapper';
import { DataTable } from '../../../../styles/ui/Table';
import { CellLink } from '../../../../styles/ui/Link';
import { SHIPMENT_STATUS } from '../constants';
import { formatDateTimeBr } from '../utils/format';

// =============================================================================
// Movimentos de estoque gerados pela transferência.
//
// Fecha o círculo com o módulo Movements: lá o movimento aponta para a
// transferência (campo `transfer`), aqui a transferência aponta para os
// movimentos. Antes, o elo só existia numa direção.
//
// SEM chamada nova: os identificadores já vêm em cada remessa — a lista é
// derivada de `order.shipments`. A data também é derivada, não inventada: o
// `out` nasce na expedição e o `in` na conferência, então cada um herda o
// carimbo do próprio evento da remessa.
//
// ⚠️ O MOVIMENTO DE REVERSÃO NÃO ENTRA (correção 2026-08-22). `reversion_
// movement_id` existe no contrato, mas movimento de reversão é **recurso de
// sistema**, não documento de negócio: ele é o contra-lançamento que o motor de
// estoque cria ao excluir um movimento (excluiu uma entrada de 2 un.? nasce uma
// saída de 2 un. para corrigir o saldo físico). As regras de negócio impedem
// consultá-lo, então exibir o id — ainda mais como link — oferecia ao operador
// um destino que ele não pode abrir.
//
// O fato continua visível sem ele: a remessa cancelada mostra a tarja de
// reversão no próprio card, e o movimento de saída original aparece aqui
// marcado como estornado (e, no detalhe dele, como EXCLUÍDO com o motivo).
// =============================================================================

const ROLES = [
   {
      key: 'out',
      idField: 'out_movement_id',
      atField: 'dispatched_at',
      label: 'Saída da origem',
      hint: 'Baixa o estoque no PDV de origem, na expedição.',
      tone: 'out',
   },
   {
      key: 'in',
      idField: 'in_movement_id',
      atField: 'received_at',
      label: 'Entrada no destino',
      hint: 'Entra pelo CONTADO na conferência, não pelo expedido.',
      tone: 'in',
   },
];

const EffectTag = styled.span`
   display: inline-block;
   padding: 2px 8px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 800;
   white-space: nowrap;
   border: 1px solid
      ${({ $tone }) => {
         if ($tone === 'out') return 'rgba(211,47,47,0.32)';
         if ($tone === 'in') return 'rgba(46,125,50,0.32)';
         return 'rgba(0,0,0,0.18)';
      }};
   background: ${({ $tone }) => {
      if ($tone === 'out') return 'rgba(211,47,47,0.09)';
      if ($tone === 'in') return 'rgba(46,125,50,0.09)';
      return 'rgba(0,0,0,0.05)';
   }};
   color: ${({ $tone }) => {
      if ($tone === 'out') return '#b71c1c';
      if ($tone === 'in') return '#1b5e20';
      return 'rgba(0,0,0,0.6)';
   }};
`;

// Estorno dito no movimento estornado — o contra-lançamento em si é interno.
const RevertedTag = styled.span`
   display: inline-block;
   margin-left: 8px;
   padding: 2px 8px;
   border-radius: 999px;
   font-size: 10.5px;
   font-weight: 800;
   white-space: nowrap;
   border: 1px solid rgba(211, 47, 47, 0.32);
   background: rgba(211, 47, 47, 0.09);
   color: #b71c1c;
`;

const FootNote = styled.p`
   margin: 10px 0 0;
   font-size: 12px;
   line-height: 1.5;
   color: rgba(0, 0, 0, 0.55);
`;

// Deriva a lista a partir das remessas, na ordem em que os movimentos foram
// criados (id crescente).
export function buildMovementRows(shipments = []) {
   const rows = [];

   shipments.forEach((shipment) => {
      // Remessa cancelada teve o `out` estornado pela reversão. A reversão em
      // si não é listável (ver cabeçalho), então o estorno é dito como
      // ATRIBUTO do movimento estornado, que é consultável.
      const isReverted = shipment.status === SHIPMENT_STATUS.CANCELLED;

      ROLES.forEach((role) => {
         const movementId = shipment[role.idField];
         if (!movementId) return;
         rows.push({
            movementId,
            shipmentId: shipment.id,
            at: shipment[role.atField] ?? null,
            isReverted: isReverted && role.key === 'out',
            revertedAt: isReverted ? shipment.cancelled_at ?? null : null,
            ...role,
         });
      });
   });

   return rows.sort((a, b) => Number(a.movementId) - Number(b.movementId));
}

export default function GeneratedMovements({ rows, canOpenMovement = false }) {
   return (
      <>
         <TableWrapper>
            <DataTable>
               <thead>
                  <tr>
                     <th className="center">Movimento</th>
                     <th>Efeito no estoque</th>
                     <th className="center">Remessa</th>
                     <th>Quando</th>
                  </tr>
               </thead>
               <tbody>
                  {rows.map((row) => (
                     <tr key={`${row.shipmentId}-${row.key}`}>
                        <td className="center">
                           {/* Sem permissão de leitura de movimentos o número
                               continua visível (é rastreabilidade), mas não
                               vira link para uma tela que ele não abre. */}
                           {canOpenMovement ? (
                              <CellLink to={`/movements/${row.movementId}`}>
                                 #{row.movementId}
                              </CellLink>
                           ) : (
                              `#${row.movementId}`
                           )}
                        </td>
                        <td>
                           <EffectTag $tone={row.tone} title={row.hint}>
                              {row.label}
                           </EffectTag>
                           {row.isReverted && (
                              <RevertedTag
                                 title={`Estornado no cancelamento da remessa em ${formatDateTimeBr(
                                    row.revertedAt
                                 )} — o estoque voltou à origem.`}
                              >
                                 Estornado
                              </RevertedTag>
                           )}
                        </td>
                        <td className="center">#{row.shipmentId}</td>
                        <td>{formatDateTimeBr(row.at)}</td>
                     </tr>
                  ))}
               </tbody>
            </DataTable>
         </TableWrapper>

         <FootNote>
            Estes movimentos aparecem também na consulta de movimentos, com o
            documento <strong>TRANSFERÊNCIA</strong> e o número desta ordem. Eles
            só podem ser alterados pelo fluxo de transferência.
         </FootNote>
      </>
   );
}

GeneratedMovements.propTypes = {
   rows: PropTypes.arrayOf(
      PropTypes.shape({
         movementId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
         shipmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
         key: PropTypes.string,
         label: PropTypes.string,
         tone: PropTypes.string,
         at: PropTypes.string,
         isReverted: PropTypes.bool,
         revertedAt: PropTypes.string,
      })
   ).isRequired,
   canOpenMovement: PropTypes.bool,
};
